/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { CurrencyPipe, formatPercent } from '@angular/common';
import { Component, inject } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ReplaySubject, Observable, of, forkJoin } from 'rxjs';
import { concatMap, first, map, tap } from 'rxjs/operators';
import { uuid } from '@src/app/model/abstract-object';
import { Attachment } from '@src/app/model/attachment';
import { BCode } from '@src/app/model/bcode';
import { Cycle } from '@src/app/model/cycle';
import { Field } from '@src/app/shared/field/Field';
import { Period } from '@src/app/model/period';
import { Schedule } from '@src/app/model/schedule';
import { PreferredSupplier } from '@src/app/model/supplier';
import { Txn } from '@src/app/model/txn';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { AppFormControl } from '@src/app/shared/form/app-form-control';
import { FieldSet } from '@src/app/shared/form/field-set/field-set.component';
import { AttachmentListComponent } from '@src/app/shared/form/file/attachment-list/attachment-list.component';
import { AttachmentService } from '@src/app/shared/form/file/attachment.service';
import { FormButtonComponent } from '@src/app/shared/form/form-button/form-button.component';
import { FormDateComponent } from '@src/app/shared/form/form-date/form-date.component';
import { FormError } from '@src/app/shared/form/form-error/form-error.component';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { IFormAction } from '@src/app/shared/form/form.component';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { GridField } from '@src/app/shared/grid/grid-field';
import { GridRow } from '@src/app/shared/grid/grid-row';
import { GridControl } from '@src/app/shared/grid/grid-control';
import { validateDate, required } from '@src/app/shared/validators';
import { BCodeService } from '@src/app/modules/budget/bcode.service';
import { CycleService } from '@src/app/modules/budget/cycle.service';
import { PeriodService } from '@src/app/modules/budget/period.service';
import { ScheduleService } from '@src/app/modules/budget/schedule.service';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { PurchaseService } from '@src/app/modules/txn/purchase.service';
import { FormDateTimeComponent } from '@src/app/shared/form/form-date-time/form-date-time.component';
import { PicklistField } from '@src/app/shared/field/PicklistField';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { FormComboBoxComponent } from '@src/app/shared/form/form-combo-box/form-combo-box.component';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { SupplierService } from '@src/app/modules/supply/supplier.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { OmcSettingsService } from '@src/app/pages/omc-settings/omc-settings.service';
import { OmcSettings } from '@src/app/model/OmcSettings';
import { AttachmentGridComponent } from '@src/app/shared/form/file/attachment-grid/attachment-grid.component';
import { DialogOptions } from '@src/app/shared/dialogs/pick-dialog/pick-dialog.component';
import { AbstractHttpService } from '@src/app/shared/abstract-http.service';
import { AllocateCreditNoteAction } from '@src/app/modules/txn/purchase-page/AllocateCreditNoteAction';
import { UnApproveForPaymentAction } from '@src/app/modules/txn/purchase-page/UnApproveForPaymentAction';
import { CreditInvoiceAction } from '@src/app/modules/txn/purchase-page/CreditInvoiceAction';
import { ApproveForPaymentAction } from '@src/app/modules/txn/purchase-page/ApproveForPaymentAction';
import { NrHelper } from '@src/app/shared/NrHelper';
import { FormGroup, ValidationErrors } from '@angular/forms';
import { WriteDownAccrualHelper } from '@src/app/modules/txn/purchase-page/WriteDownAccrualHelper';
import { BankAccountService } from '@src/app/modules/txn/bank-account.service';
import { BankItem } from '@src/app/model/bankAccount';
import { FormNavLinkComponent } from '@src/app/shared/form/form-nav-button/form-nav-link.component';
import { ServiceRequestPageNavRoute } from '@src/app/routes/service-request-page-nav';
import { ServiceRequest } from '@src/app/model/serviceRequest';
import { RelatedRequestsComponent } from '@src/app/shared/related-requests/related-requests.component';
import { TenantService } from '@src/app/shared/tenant.service';
import { PurchasePageNavRoute } from '@src/app/routes/purchase-page-nav';
import { VatRateService } from '@src/app/modules/txn/VATRate.service';

export class PurchaseDialogOptions implements DialogOptions {
    /** Real Params */
    supplier?: PreferredSupplier;
    txn?: Txn;
    typeId: number;
    reverseInvoice?: Txn;
    bankId?: uuid;
    bankItem?: BankItem;

    config: FormConfig;
    service: AbstractHttpService;
    id?: uuid;
    forceTeamId?: uuid;
    width?: number;
    height?: number;
    showSearch?: boolean;
    addOnly?: boolean;
    hideTabs?: boolean;
    condensed?: boolean;
}

@Component({
    selector: 'app-purchase-page',
    templateUrl: './purchase-page.component.html',
    styleUrls: ['./purchase-page.component.scss'],
    imports: [FormPageComponent, CurrencyPipe]
})
export class PurchasePageComponent extends AbstractPageComponent {
    dataSvc = inject(PurchaseService);
    bCodeSvc = inject(BCodeService);
    protected activeRoute = inject(ActivatedRoute);
    private router = inject(Router);
    protected supplierSvc = inject(SupplierService);
    private bankAccountSvc = inject(BankAccountService);
    protected cycleSvc = inject(CycleService);
    protected periodSvc = inject(PeriodService);
    private attachmentSvc = inject(AttachmentService);
    protected scheduleSvc = inject(ScheduleService);
    private vatRateSvc = inject(VatRateService);
    protected cds = inject(ConfirmDialogService);
    private currentUserSvc = inject(CurrentUserService);
    dialogRef = inject<MatDialogRef<PurchasePageComponent>>(MatDialogRef, { optional: true });
    dialogOptions = inject<PurchaseDialogOptions>(MAT_DIALOG_DATA, { optional: true });

    omcSettingsSvc = inject(OmcSettingsService);
    tenantSvc = inject(TenantService);
    readonly path = Txn.TYPE.PURCHASE.code

    needsInvoicePDF = true;
    vatRegistered = false;
    vatControlAccountId: uuid;
    hasPrepays = false;
    configReady = new ReplaySubject<null>(1);
    currentCycle: Cycle;
    cycles: Cycle[] = [];
    periods: Period[] = [];
    schedules: Schedule[];
    bcodes: BCode[];

    prepays: BCode;
    expenseCodes: BCode[] = [];

    itemTotal: 0;
    itemOutstanding: 0;

    /** Cycle & Period just for queries */
    cycleField: PicklistField = FormPicklistComponent.make('Cycle', 'txnCycleId', 'txnCycle',
        { items: this.cycles}, { formColumn: 2, visible: Field.noShow, sendServer: false }
    );
    periodField: PicklistField = FormPicklistComponent.make('Period', 'txnPeriodId', 'txnPeriod',
        { items: this.periods }, {visible: Field.noShow, sendServer: false}
    );

    txnDateField: Field = FormDateComponent.make('Transaction Date', 'txnDate', {
        cellOpts: { width: '2%' }, formColumn: 1,
        validators: [this.periodSvc.validateTxnDate(this.periods), required],
        valueChanges: this.invoiceValueChanges.bind(this)
    });

    svcFromField = FormDateComponent.make('Services From', 'svcFrom', {
        cellOpts: { width: '2%' }, formColumn: 2, formLine: 'serviceDates',
        validators: [this.validateServiceDates.bind(this)]
    });

    svcToField = FormDateComponent.make('Services To', 'svcTo', {
        cellOpts: { width: '2%' }, formColumn: 2, formLine: 'serviceDates',
        validators: [this.validateServiceDates.bind(this)],
    });
    // Remember - disabling would bypass validation!
    amountField = FormNumberComponent.make("Total", "amount",
        { format: 'currency', width: 9, formatParms: '1.2-2' },
        { formRow: 1, formColumn: 1, readonly: true, validators: [this.validateInvoiceAmount.bind(this)],
            calculateValue: (o: Txn) => Math.abs(o.debit - o.credit), sendServer: false,
            tableStyle: this.amountTableStyler.bind(this)
         }
    );

    vatTotalField = FormNumberComponent.makeCurrency("VAT", "vatAmount",
        { formRow: 1, formColumn: 2, readonly: true, sendServer: false, visible: Field.noShow}
    );

    /* debit &  credit just for sending amounts to server, changing 'value' property live causes issues*/
    debitField = FormNumberComponent.makeCurrency("Debit", "debit",{visible: Field.noShow});
    creditField = FormNumberComponent.makeCurrency("Credit", "credit", { visible: Field.noShow });

    outstandingField = FormNumberComponent.make("Outstanding", "outstanding",
        { format: 'currency', width: 9, formatParms: '1.2-2' },
        { formRow: 1, formColumn: 4, readonly: true, disable: true, sendServer: false, condensed: true }
    );

    attachedFiles = AttachmentListComponent.make('Attached Invoice', 'attachments',
        { service: this.dataSvc, allowNew: true, smallAddBox: true },
        { formColumn: 4, valueChanges: this.invoiceValueChanges.bind(this) }
    );

    attachmentGridEdit = AttachmentGridComponent.make(this.dataSvc, { formColumn: 4, name: 'attachmentForEdit'}).showSimpleVersion({deletes: false});

    supplierField = FormComboBoxComponent.make('Supplier', 'supplierId', 'supplier',
        { service: this.supplierSvc, refreshes: [this.supplierChanges.bind(this)] },
        { validators: [required], formColumn: 2 }
    );

    bankAccountField = FormPicklistComponent.make('Bank Account', 'bankAccountId', null,
        { service: this.bankAccountSvc },
        { validators: [this.bankRequired.bind(this)], formColumn: 2, visible: Field.noShow }
    );

    lineItemsField = new GridField({
        field:
        {
            label: $localize`Purchase Details`, value: 'lineItems',
            visible: Field.formOnly, formRow: 2
        },
        rowFactory: this.childRowFactory.bind(this),
        objFactory: this.newChildTxn.bind(this),
    });

    journalGridField = new GridField({
        field:
        {
            label: $localize`Journals`, value: 'journalItems',
            visible: Field.noShow, formRow: 2, readonly: true, sendServer: false
        },
        rowFactory: this.journalItemRowFactory.bind(this),
    });
    allocationsGridField = new GridField({
        field:
        {
            label: $localize`Allocations`, value: 'allocations',
            visible: Field.noShow, formRow: 2, readonly: true, sendServer: false
        },
        rowFactory: this.allocationGridRowFactory.bind(this),
    });

    paymentGrid = new GridField({
        field:
            { label: 'Payments Made', value: 'relations', visible: Field.formOnly, formRow: 2, sendServer: false, readonly: true },
        rowFactory: () => [
            FormPicklistComponent.make('Type', 'typeId', 'type', { items: Txn.TYPES }, { readonly: true }),
            FormNavLinkComponent.makeTxnLink('Ref'),
            FormDateComponent.make('Date', 'txnDate', { cellOpts: { width: '2%' }, formColumn: 2, readonly: true }),
            FormNumberComponent.make("Amount", "debit", { format: 'currency', width: 9, formatParms: '1.2-2' }, {
                readonly: true, calculateValue: (o: Txn) => o.debit - o.credit
            }),
            FieldMaker.notes({readonly: true}),
            FieldMaker.spacer(),
            FieldMaker.rev(),
        ]
    });

    approvedField = FormDateTimeComponent.make('Approved For Payment', 'approvedAt', { readonly: true, sendServer: false, formColumn: 4, condensed: true });
    approvedByField = FormTextComponent.make('Approved By', 'approverName', {disable: true, formColumn: 4, condensed:true, sendServer: false})

    refField = FormNumberComponent.make('Reference', 'refNr', { format: 'plain' }, { cellOpts: { heading: 'Ref' }, readonly: true });

    typeIdField = FormPicklistComponent.make('Type', 'typeId', 'type', { items: Txn.TYPES }, { readonly: true });

    reverseIdField = FieldMaker.idHolder('reverseId');



    workOrdersField = RelatedRequestsComponent.make({
        label: $localize`Work Orders`,
        value: 'requests',
        formColumn: 3,
        navRoute: ServiceRequestPageNavRoute,
        linkLabel: $localize`Link Work Order`,
        linkPrompt: $localize`These work orders are open for this supplier - does this invoice apply to any?`,
        linkFilter: () => ({
            supplierId: this.supplierField.control?.value,
            srStatus: `${ServiceRequest.srOpen.id},${ServiceRequest.srResolved.id}`,
        }),
        linkRequest: (sr: ServiceRequest) => this.linkWorkOrder(sr),
        unlinkRequest: (sr: ServiceRequest) => this.unlinkWorkOrder(sr),
    }).showSimpleVersion();

    /**
     * The ones picked while adding, where there is no invoice for a link row to name yet - sent with
     * the new invoice for ApChargeServices to write, each with the answer the user gave to the link
     * confirmation. Off for an edit: by then each link is a row of its own, added and removed as it
     * is picked.
     */
    requestLinksField = FieldMaker.idHolder('requestLinks', { sendServer: false });

    /** The work orders picked with "Link & Accept" while adding - see requestLinksField. */
    private acceptOnSave = new Set<uuid>();

    config = new FormConfig({
        navRoute: PurchasePageNavRoute,
        docPath: 'omc-books/purchase-accounting/purchases',
        title: Txn.TYPE.PURCHASE.name,
        help: $localize`Goods and Services Purchased`,
        readonly: false,
        fieldSet: new FieldSet({
            fields: [

                FieldMaker.id(), // this.idField,
                FieldMaker.rev(), //this.revField,
                this.typeIdField,
                this.refField,
                this.bankAccountField,                
                this.supplierField,
                FormTextComponent.make('globalSupplierId', 'globalSupplierId', {
                    visible: Field.noShow,
                    refresh: (o: PreferredSupplier, ctl: AppFormControl) => ctl.setValue(o.globalSupplierId)
                }),
                FormTextComponent.make('Supplier Reference', 'reference', { validators: [required], formColumn: 2 }),
                FormTextComponent.make('ledgerId', 'ledgerId', { visible: Field.noShow }),

                /* txtDateField Validator overwrites period info, need this order! */
                this.cycleField,
                this.periodField,
                this.txnDateField,

                this.svcFromField,
                this.svcToField,

                this.amountField,
                this.vatTotalField,
                this.debitField, this.creditField,

                FieldMaker.notes({ formColumn: 3 }),
                this.workOrdersField,
                this.requestLinksField,

                this.reverseIdField,

                this.attachedFiles,
                this.attachmentGridEdit,
                this.approvedField,
                this.approvedByField,
                this.outstandingField,

                this.lineItemsField,
                this.journalGridField,
                this.paymentGrid,
                this.allocationsGridField,
            ],
            formValidator: this.needsAttachment.bind(this),
            formLayout: [
                { cells: [{ width: '10%' }, { width: '30%' }, { width: '30%' }, { width: '30%' }] },
                { cells: [{ colspan: 5, pageTab: 'yes' }] }
            ],
        }),
        service: this.dataSvc,
        mode: 'list',
        objectFactory: this.newFactory.bind(this),
        configReady: this.configReady,
        beforeEdit: this.beforeEdit.bind(this),
        beforeSave: this.beforeSave.bind(this),
        deleteMsg: Txn.DELETE_MSG,
        deleteReasonNeeded: true,
        afterSave: this.afterSave.bind(this),
        actions: this.getActions(),
        beforeList: this.beforeList.bind(this),
        newOptions: this.dataSvc.getNewOptions(),
    });
    creditorsId: uuid;
    accrualsId: uuid;

    constructor()  {

        super();
        const cycleSvc = this.cycleSvc;
        const vatRateSvc = this.vatRateSvc;
        const currentUserSvc = this.currentUserSvc;

        // Mysteriously, without the .pipe(first())s the currentUser & defaultBCodes not firing and forkJoin never completes
        forkJoin({
            currentUser: currentUserSvc.getCurrentUser().pipe(first()),
            schedules: this.scheduleSvc.get(true),
            bcodes: this.bCodeSvc.get(true),
            periods: this.periodSvc.get<Period>(true),
            cycles: this.cycleSvc.get<Cycle>(true),
            omcSettings: this.omcSettingsSvc.getOne<OmcSettings>('omcSettings', null).pipe( concatMap(
                omcSettings => {
                    if (vatRateSvc.vatRegistered(omcSettings)) {
                        return this.vatRateSvc.getVatRates().pipe( map( () => omcSettings))
                    }
                    return of(omcSettings)
                }
            )),
            defBCodes: this.currentUserSvc.getDefaultBCodes().pipe(first())
        }).subscribe(result => {
                this.currentUser = result.currentUser;
                this.cycles.length = 0;
                result.cycles.forEach( c => this.cycles.push(c));
                this.cycleField.setPicklistItems(this.cycles);
                this.currentCycle = cycleSvc.getCurrentCycle(result.cycles);
                this.periodField.setPicklistItems(this.currentCycle.periods);
                this.currentPeriod = cycleSvc.getCurrentPeriod(this.currentCycle);
                this.schedules = result.schedules as Schedule[];
                this.bcodes = result.bcodes as BCode[];
                this.periods.length = 0;
                result.periods.sort(Period.sorter).forEach(p => this.periods.push(p));
                this.setupAllowedBCodes();
                this.setSettings(result.omcSettings);
                this.creditorsId = this.currentUserSvc.getDefaultBCode('Creditors').bCodeId;
                this.accrualsId = this.currentUserSvc.getDefaultBCode('Accruals').bCodeId;
                this.vatControlAccountId = this.currentUserSvc.getDefaultBCode('VatControl')?.bCodeId;
                this.configReady.next(null);
            }
        );
    }

    validateInvoiceAmount(c: AppFormControl): ValidationErrors {
        if (this.dialogOptions?.bankItem) {
            const amt = 0 - this.dialogOptions.bankItem.transactionAmount
            if (!NrHelper.equal(c.value, amt)) {
                const et = $localize `Amount must match ${NrHelper.toFormattedCurrency(amt)}`
                return FormError.reportError('Required', et );
            }
        } else {
            if ((c?.value === 0)) {
                return FormError.reportError('Required', 'Amount required');
            }
        }
        return null;
    }

    setupAllowedBCodes() {
        const debtorsId: uuid = this.currentUserSvc.getDefaultBCode('Debtors').bCodeId;
        const creditorsId: uuid = this.currentUserSvc.getDefaultBCode('Creditors').bCodeId;
        const surplusId: uuid = this.currentUserSvc.getDefaultBCode('RetainedProfit').bCodeId;
        const noPost = [debtorsId, creditorsId, surplusId]
        for (const bc of this.bcodes) {
            if (bc.typeId === BCode.TYPE.EXPENSE.id
            || bc.typeId === BCode.TYPE.ASSET.id
            || bc.typeId === BCode.TYPE.CAPITAL.id) {
                if (noPost.indexOf(bc.id) < 0) {
                    this.expenseCodes.push(bc);
                }
            }
        }

        // Allow invoice be created to offset accrual entries
        const accrualsId: uuid = this.currentUserSvc.getDefaultBCode('Accruals').bCodeId;
        const accrualBC = this.bcodes.find(o => o.id === accrualsId);

        this.expenseCodes.push(accrualBC)
    }

    setSettings(omcSettings: OmcSettings) {
        if (omcSettings?.purchaseAttachments === 'optional') {
            this.needsInvoicePDF = false;
        } else {
            this.needsInvoicePDF = true;
        }

        if (this.vatRateSvc.vatRegistered(omcSettings)) {
            this.vatRegistered = true;
            this.vatTotalField.visible = Field.formOnly;
        } else {
            this.vatRegistered = false;
            this.vatTotalField.visible = Field.noShow;
        }
    }

    beforeList(items: Txn[]) {
        this.itemOutstanding = 0;
        this.itemTotal = 0;
        items.forEach(p => {
            this.itemOutstanding += p.outstanding;
            this.itemTotal += p.credit;
        });
        return items;
    }

    needsAttachment(formGroup: FormGroup) {
        const value = this.attachedFiles.control?.value;
        const deferred = this.attachedFiles.fileOpts.deferredFiles;
        const typeId = formGroup.controls.typeId;
        const accrual = typeId?.value === Txn.TYPE.PURCHASE_ACCRUAL.id || typeId?.value === Txn.TYPE.PURCHASE_CN_ACCRUAL.id;
        if (value && value.length === 0 && deferred && deferred.length === 0 && (this.needsInvoicePDF && !accrual) ) {
            return FormError.reportError('needsAttachment',
                $localize`Attach PDF or create accrual instead`);
        }
        return null;
    }

    isAccrualToReverse(o: Txn) {
        const cTypeId = this.currentTypeId();
        if ([Txn.TYPE.PURCHASE.id, Txn.TYPE.CASH_PURCHASE.id].includes(cTypeId) 
            && o.typeId === Txn.TYPE.PURCHASE_ACCRUAL.id && o.outstanding > 0) {
            return true;
        } if (cTypeId === Txn.TYPE.PURCHASE_CN.id && o.typeId === Txn.TYPE.PURCHASE_CN_ACCRUAL.id && o.outstanding < 0) {
            return true;
        }
        return false;
    }

    supplierChanges(o: PreferredSupplier) {

        this.supplierSvc.getOne(o.id, null).subscribe( ps => {
            const txns = [... (this.lineItemsField.control as GridControl).gridRows()];

            if (txns) {
                for (const tRow of txns) {
                    tRow.delete();
                }
            }

            this.workOrdersField.clearRequests();
            this.acceptOnSave.clear();

            const accruals = ps.transactions.filter( o => this.isAccrualToReverse(o));
            if (accruals.length > 0) {
                const wdah = new WriteDownAccrualHelper(accruals, o)
                wdah.openDialog().subscribe( (revs: Txn[]) => {
                    this.workOrdersField.link('offeredAutoMagically');
                    if (revs) {
                        revs.forEach( r => {
                            if (r?.allocate) {
                                const childTxn = new Txn();
                                const cTypeId = this.currentTypeId()
                                childTxn.bCodeId = this.accrualsId;
                                if (cTypeId === Txn.TYPE.PURCHASE.id) {
                                    childTxn.debit = r.allocate;
                                } else {
                                    childTxn.credit = r.allocate;
                                }
                                childTxn.typeId = Txn.TYPE.PURCHASE_ACCRUAL_REV.id;
                                childTxn.notes = $localize `Reversal of ${r.reference}`
                                childTxn.relatedId = r.id;

                                this.lineItemsField.control.addRow(childTxn, true, false);
                            }
                        })
                        this.invoiceValueChanges();
                    }
                });
            } else {
                this.workOrdersField.link('offeredAutoMagically');
            }
            const bcodesDone = new Map<uuid, BCode>();

            for (const st of ps.template) {
                if (!bcodesDone.has(st.bCodeId)) {
                    const bCode = this.bcodes.find( o => o.id === st.bCodeId);
                    const childTxn = this.createChildItem();
                    childTxn.bCode = bCode
                    childTxn.bCodeId = st.bCodeId;
                    childTxn.vatCode = bCode.vatCode;
                    if (bCode.schedule.length > 1) {
                        // This is an item we split by default;
                        // We want just one line, with no schedule value
                        // so user can enter amount and have it split for them
                        bcodesDone.set(st.bCodeId, bCode);
                    } else {
                        childTxn.schedule = this.schedules.find( o => o.id === st.scheduleId );
                        childTxn.scheduleId = st.scheduleId;
                    }
                    this.lineItemsField.control.addRow(childTxn, true, false);
                }
            }
        });
    }

    private linkWorkOrder(sr: ServiceRequest): Observable<unknown> {
        return this.confirmLinkWorkOrder(sr).pipe(concatMap(answer => {
            if (!answer) { return of(null); }
            return this.applyWorkOrderLink(sr, answer === 'accept');
        }));
    }

    /**
     * A work order sits resolved until the charge for it is entered - this invoice or accrual - so
     * this is the natural place to answer. One still open is closed as complete on the way through;
     * see ServiceRequest::acceptAsInvoiced.
     */
    private confirmLinkWorkOrder(sr: ServiceRequest): Observable<unknown> {
        const what = $localize`Link work order ${sr.refNr} - ${sr.title} to this purchase.`;
        const accepting = $localize`Accepting it as well records that the charge for the work has been entered, and closes the work order if it is still open.`;
        return this.cds.openChoice({
            title: $localize`Link Work Order`, msg: `${what} ${accepting}`, options: [
                { name: $localize`Link & Accept Work Order`, returns: 'accept' },
                { name: $localize`Link Only`, returns: 'link' },
            ]
        }).pipe(first());
    }

    /**
     * A link row names the invoice, so while one is being added there is nothing to write yet: the
     * work order is kept on the panel and goes up with the new invoice - see requestLinksField. On a
     * saved invoice the link is written as it is picked, and the request comes back with whatever
     * status accepting left it on.
     */
    private applyWorkOrderLink(sr: ServiceRequest, accept: boolean): Observable<unknown> {
        const txn = this.page.form.focusItem as Txn;
        if (!txn?.id) {
            this.markAcceptOnSave(sr.id, accept);
            return of(sr);
        }
        return this.dataSvc.linkRequest(txn, sr.id, accept).pipe(
            tap(updated => { if (updated) { Object.assign(sr, updated); } })
        );
    }

    private markAcceptOnSave(requestId: uuid, accept: boolean) {
        if (accept) {
            this.acceptOnSave.add(requestId);
        } else {
            this.acceptOnSave.delete(requestId);
        }
    }

    private unlinkWorkOrder(sr: ServiceRequest): Observable<unknown> {
        const txn = this.page.form.focusItem as Txn;
        const msg = $localize`Unlink work order ${sr.refNr} - ${sr.title} from this purchase`;
        return this.cds.openChoice({ title: $localize`Unlink Work Order`, msg,
            options: [{ name: $localize`Unlink`, returns: true }] }).pipe(first(),
            concatMap(yes => {
                if (!yes) { return of(null); }
                this.acceptOnSave.delete(sr.id);
                return txn?.id ? this.dataSvc.unlinkRequest(txn, sr.id) : of(sr);
            })
        );
    }

    beforeSave() {

        this.requestLinksField.control.setValue(this.workOrdersField.srList.map(
            sr => ({ requestId: sr.id, accept: this.acceptOnSave.has(sr.id) })));

        const t = this.page.form.focusItem as Txn;
        const cn = (t.typeId === Txn.TYPE.PURCHASE_CN.id || t.typeId === Txn.TYPE.PURCHASE_CN_ACCRUAL.id);
        const lineItems = this.lineItemsField.control.gridRows();
        const deleteItems : GridRow[] = [];

        for (const ctl of lineItems) {

            const o = (ctl.getFormValue() as Txn);
            const oAmt = o['amount'];
            if (!oAmt) {
                deleteItems.push(ctl);
            } else {
                if (cn) {
                    ctl.controls.debit.setValue(0);
                    ctl.controls.credit.setValue(oAmt);
                } else {
                    ctl.controls.debit.setValue(oAmt);
                    ctl.controls.credit.setValue(0);
                }
            }
        }
        deleteItems.forEach( di => di.delete());

        if (cn) {
            this.debitField.control.setValue(this.amountField.control.value);
            this.creditField.control.setValue(0);
        } else {
            this.debitField.control.setValue(0);
            this.creditField.control.setValue(this.amountField.control.value);
        }
    }


    amountTableStyler(o: Txn) {
        const oType = Txn.getType(o?.typeId);
        const style: Record<string, string> = {};
        if (oType?.color) {
            style.color = oType.color
        }
        if (o?.outstanding) {
            style['font-weight'] = 'bold';
        }
        return style;
    }

    filesMode(editMode) {
        this.attachedFiles.visible = editMode ? Field.noShow : Field.formOnly;
        this.attachmentGridEdit.visible = editMode ? Field.formOnly : Field.noShow;
    }

    beforeEdit(o: Txn) {
        this.filesMode(true);
        this.setupType(o.type);
        this.supplierField.disableControl();
        if (o.typeId === Txn.TYPE.CASH_PURCHASE.id) {
            this.bankAccountField.visible = Field.formOnly;
        } else {
            this.bankAccountField.visible = Field.noShow;
        }
        this.paymentGrid.visible = Field.formOnly;
        this.allocationsGridField.visible = Field.noShow;
        this.reverseIdField.sendServer = false;
        this.requestLinksField.sendServer = false;
        this.hasPrepays = false;
        this.approvedByField.visible = Field.showAll;
        this.approvedField.visible = Field.showAll;

        if (o === null) {
            o = new Txn(); // Item was not found, will be reported - avoid any further errors
        }
        o.lineItems = [];
        o.journalItems = [];
        o.allocations = [];
        o.requests = o.requests ?? [];
        for (const d of o.details) {
            if (d.typeId === Txn.TYPE.PURCHASE_ITEM.id
            || d.typeId === Txn.TYPE.CASH_PURCHASE_ITEM.id
            || d.typeId === Txn.TYPE.PURCHASE_CN_ITEM.id
            || d.typeId === Txn.TYPE.PURCHASE_ACCRUAL_ITEM.id
            || d.typeId === Txn.TYPE.PURCHASE_CN_ACCRUAL_ITEM.id) {
                o.vatAmount += d.vatAmount;
                o.lineItems.push(d);
            } else if (d.typeId === Txn.TYPE.PURCHASE_CN_USED.id
            || d.typeId === Txn.TYPE.PURCHASE_CN_APPLY.id
            || d.typeId === Txn.TYPE.PURCHASE_ACCRUAL_REV.id) {
                o.allocations.push(d)
                if (d.typeId === Txn.TYPE.PURCHASE_ACCRUAL_REV.id) {
                    o.lineItems.push(d); // Needed in both lists, lineItems for total, allocations for link
                }
                this.allocationsGridField.visible = Field.formOnly;
            } else {
                o.journalItems.push(d);
                if (d.typeId === Txn.TYPE.PREPAY_ASSET.id) {
                    this.hasPrepays = true;
                }
            }
        }
        if (o.journalItems.length > 0) {
            this.journalGridField.visible = Field.formOnly;
        }
        return o;
    }

    /*
    * After saving, if there was an attachment, and we are adding another, we probably want to go back to purchase inbox.
    */
    afterSave() {
        const cp = this.page.form.currentParams;
        if (this.config.mode === 'new' && cp && cp['attach']) {
            const tenantId = this.tenantSvc.getCurrentTenantId();
            // Importing the PurchaseUploadsPage to get the navRoute causes weird errors initializing upload component...
            this.router.navigate([`/${tenantId}/${Txn.TYPE.PURCHASE.code}/uploads`]);
        }
    }

    journalItemRowFactory(): Field[] {
        return [
            FieldMaker.id(),
            FormPicklistComponent.make('Type', 'typeId', 'type',
                { items: Txn.TYPES },
                { cellOpts: { width: '20em' }, readonly: true }),
            FormDateComponent.make('txnDate', 'txnDate', {readonly: true}),
            FormPicklistComponent.make('Period', 'txnPeriodId', 'txnPeriod',
                { items: this.periods },
                { cellOpts: { width: '10em' }, readonly: true }),

            FormPicklistComponent.make('Account', 'bCodeId', 'bCode',
                {
                    items: this.bcodes,
                },
                { cellOpts: { width: '20em' }, readonly: true }),

            FormPicklistComponent.make('Schedule', 'scheduleId', 'bCode',
                { items: this.schedules },
                { cellOpts: { width: '20em' }, readonly: true }),

            FormNumberComponent.make("Debit", "debit", { format: 'currency', width: 9, formatParms: '1.2-2' }, {readonly:true}),
            FormNumberComponent.make("Credit", "credit", { format: 'currency', width: 9, formatParms: '1.2-2' }, { readonly: true }),
            FieldMaker.notes({ cellOpts: { width: '30em' }, readonly: true }),
            FieldMaker.rev(),
        ];
    }

    isApplied(o:Txn) {
        if (o.typeId === Txn.TYPE.PURCHASE_CN_APPLY.id && o.debit > 0) {
            return true;
        }
        return false;
    }

    removeAllocation(o) {
        if (o.typeId === Txn.TYPE.PURCHASE_CN_APPLY.id && o.debit > 0) {
            const title = $localize`Remove Allocation`;
            const msg = $localize`
                Do you wish to remove this allocation of ${NrHelper.toFormattedCurrency(o.debit)}
                from purchase invoice ${o.related.refNr}. The invoice will then have this amount due
                and the credit note will have the amount available to allocate elsewhere.
            `;
            this.cds.open(title, msg, () => {
                this.dataSvc.cnRemoveAllocation(o).subscribe( x => {
                    if (x) {
                        this.page.form.reloadContent(x);
                    }
                })
            })

        } else {
            console.log('Cannot remove allocaiton ', o);
        }
    }

    allocationGridRowFactory(o: Txn): Field[] {
        const removeBtn = FormButtonComponent.makeIconButton('Remove',
            (o: Txn) => this.isApplied(o) ? 'delete' : '',
            $localize`Remove Allocation`,
            () => this.removeAllocation(o)
        );
        removeBtn.btnOpts.color = 'warn';
        return [
            FieldMaker.id(),
            FormPicklistComponent.make('Type', 'related.typeId', 'related.type',
                { items: Txn.TYPES },
                { sendServer: false, readonly: true, disable: true,
                    cellOpts: { minWidth: "9em" }, calculateValue: (o:Txn) => o.related ? o.related.typeId : o.typeId}
            ),
            FormNavLinkComponent.makeTxnLink('Ref', true),
            FormDateComponent.make('txnDate', 'txnDate', {readonly: true}),
            FormPicklistComponent.make('Period', 'txnPeriodId', 'txnPeriod',
                { items: this.periods },
                { cellOpts: { width: '10em' }, readonly: true }),

            FormPicklistComponent.make('Account', 'bCodeId', 'bCode',
                {
                    items: this.expenseCodes,
                },
                { cellOpts: { width: '20em' }, readonly: true }),

            FormDateTimeComponent.make('Created', 'createdAt', {readonly: true}),

            FormNumberComponent.make("Debit", "debit", { format: 'currency', width: 9, formatParms: '1.2-2' }, {readonly:true}),
            FormNumberComponent.make("Credit", "credit", { format: 'currency', width: 9, formatParms: '1.2-2' }, { readonly: true }),
            removeBtn,
            FieldMaker.notes({ cellOpts: { width: '30em' }, readonly: true }),
            FieldMaker.rev(),
        ];
    }

    childRowFactory(o): Field[] {
        const isReversal = o?.typeId === Txn.TYPE.PURCHASE_ACCRUAL_REV.id
        const vatFields = [];
        if (this.vatRegistered) {
            vatFields.push(FormTextComponent.make('VAT Code', 'vatCode', {readonly: true}))
            vatFields.push(FormNumberComponent.make('VAT Rate', 'vatRate', {format: 'decimal', formatParms: '2.2-2'}, {readonly: true}))
            vatFields.push(FormNumberComponent.makeCurrency('VAT', 'vatAmount', {readonly: true}))
        }
        return [
            FieldMaker.id(),
            FieldMaker.rev(),
            FieldMaker.idHolder('relatedId'),
            FieldMaker.idHolder('typeId'),
            FormComboBoxComponent.make('Account', 'bCodeId', 'bCode',
                {
                    items: this.expenseCodes,
                    refreshes: [this.invoiceValueChanges.bind(this)]
                },
                { cellOpts: { width: '20em' }, readonly: isReversal }),

            FormPicklistComponent.make('Schedule', 'scheduleId', 'bCode',
                { items: this.schedules },
                { cellOpts: { width: '20em' }, validators: isReversal ? [] : [required], readonly: isReversal }),

            FormNumberComponent.make("Amount", "amount",
                { format: 'currency', width: 9, formatParms: '1.2-2', style: this.amountTableStyler(o) },
                {
                    valueChanges: this.invoiceValueChanges.bind(this),
                    calculateValue: (o: Txn) => o.debit ? o.debit : o.credit,
                    readonly: isReversal,
                    cellOpts: { width: '9em' }
                 }
            ),
            ... vatFields,
            FormNumberComponent.makeCurrency("Debit", "debit", { visible: Field.noShow }),
            FormNumberComponent.makeCurrency("Credit", "credit", { visible: Field.noShow }),
            FormTextComponent.make('Description', 'notes', { cellOpts: { width: '30em' }, readonly: isReversal }),

            FieldMaker.deleteGridRow({
                clickMethod: (ctl: AppFormControl) => {
                    if ((isReversal && !o.id) || !isReversal) {
                        ctl.getRow().delete();
                        this.invoiceValueChanges();
                    }
                }
            })
        ]
    }

    newItemType() {
        let t = Txn.TYPE.PURCHASE;// Default to purchase
        let typeId = Number(this.activeRoute.snapshot?.queryParams?.typeId);
        if (this.dialogOptions?.txn?.typeId) {
            typeId = this.dialogOptions?.txn.typeId as number;
        } else if (this.dialogRef) {
            typeId = this.dialogOptions['typeId'];
        }
        if (typeId) {
            const nt = Txn.getType(typeId);
            if (nt) {
                t = nt;
            } else {
                console.warn('Unknown TypeId', typeId);
            }
        }
        return t;
    }

    setupType(t) {
        this.config.title = t.name;
        if ((t.id === Txn.TYPE.PURCHASE_ACCRUAL.id || t.id === Txn.TYPE.PURCHASE_CN_ACCRUAL.id) && this.vatRegistered) {
            // VAT Return logic would need extensive overhaul to support purchase accruals
            this.cds.alert('NOT SUPPORTED', 'Accruals not supported for VAT registered companies, sorry', () => {
                // Opened over another dialog (closing a work order), navigating would take the user
                // off the page underneath. Closing hands an empty result back to whoever opened it.
                if (this.dialogRef) {
                    this.dialogRef.close();
                } else {
                    this.router.navigate(PurchasePageNavRoute.getListRouterLink());
                }
            })
        }
    }

    newFactory(o: Params): Observable<Txn> {

        const t = this.newItemType();
        this.setupType(t);
        if (t.id === Txn.TYPE.CASH_PURCHASE.id) {
            this.bankAccountField.visible = Field.formOnly;
        } else {
            this.bankAccountField.visible = Field.noShow;
        }        
        this.supplierField.enableControl();
        this.filesMode(false);
        this.paymentGrid.visible = Field.noShow;
        this.reverseIdField.sendServer = false;
        this.requestLinksField.sendServer = true;
        this.hasPrepays = false;
        this.config.readonly = false;

        this.approvedByField.visible = Field.noForm;
        this.approvedField.visible = Field.noForm;

        // A caller that has built the whole invoice - Credit Invoice, or closing a work order -
        // hands it over intact rather than having it rebuilt from scratch here.
        if (this.dialogOptions?.txn) {
            const seeded = this.dialogOptions.txn;
            if (this.dialogOptions.reverseInvoice) {
                this.reverseIdField.sendServer = true;
                seeded['reverseId'] = this.dialogOptions.reverseInvoice.id;
            }
            return of(seeded);
        }

        const txn = new Txn();
        if (this.dialogOptions?.bankId) {
            txn.bankAccountId = this.dialogOptions?.bankId;
        }
        this.hasPrepays = false;
        this.journalGridField.visible = Field.noShow;
        this.allocationsGridField.visible = Field.noShow;
        txn.typeId = t.id;
        if (this.dialogRef && this.dialogOptions.supplier) {
            txn.supplierId = this.dialogOptions.supplier.id;
            this.supplierChanges(this.dialogOptions.supplier);
        }

        txn.txnDate = ''; // Do not default it, make user consciously decide transaction date
        txn.svcFrom = '';
        txn.svcTo = '';

        if (o?.attach) {
            return this.attachmentSvc.getOne(o.attach, null).pipe(first()).pipe(
                map((a: Attachment) => {
                    if (a) {
                        if (!a.relatedId) {
                            txn.attachments.push(a);
                        } else {
                            console.warn('Already Attached', { a })
                            this.cds.alert($localize`Already Attached`,
                                $localize`Cannot attach to new invoice, already attached to another transaction`
                            );
                        }
                    }
                    return txn;
                })
            );
        } else {
            return of(txn);
        }
    }

    currentTypeId() {
        return (this.page.form.focusItem as Txn).typeId;
    }

    createChildItem(): Txn {
        const txn = new Txn();
        const cTypeId = this.currentTypeId();
        if (cTypeId === Txn.TYPE.PURCHASE_CN.id) {
            txn.typeId = Txn.TYPE.PURCHASE_CN_ITEM.id;
        } else if (cTypeId === Txn.TYPE.PURCHASE_ACCRUAL.id) {
            txn.typeId = Txn.TYPE.PURCHASE_ACCRUAL_ITEM.id;
        } else if (cTypeId === Txn.TYPE.CASH_PURCHASE.id) {
            txn.typeId = Txn.TYPE.CASH_PURCHASE_ITEM.id;            
        } else if (cTypeId === Txn.TYPE.PURCHASE_CN_ACCRUAL.id) {
            txn.typeId = Txn.TYPE.PURCHASE_CN_ACCRUAL_ITEM.id;
        } else {
            txn.typeId = Txn.TYPE.PURCHASE_ITEM.id;
        }

        if (this.schedules.length === 1) {
            txn.scheduleId = this.schedules[0].id;
        }

        txn.vatCode = 'x';
        txn.vatRate = 0;
        txn.vatAmount = 0;

        return txn;
    }

    newChildTxn(): Observable<Txn> {
        return of(this.createChildItem());
    }

    private isNewMode() {
        if (this.attachedFiles.visible.form) {
            return true; // in add mode...
        }
        return false;
    }
    private hasAttachments() : boolean {
        if (!this.isNewMode()) {
            return true;
        }
        if (this.attachedFiles?.control.value.length > 0 || this.attachedFiles.fileOpts.deferredFiles?.length > 0) {
            return true;
        }
        return false;
    }

    getActions(): IFormAction[] {
        return [new ApproveForPaymentAction(), new UnApproveForPaymentAction(), new CreditInvoiceAction(), new AllocateCreditNoteAction()];
    }

    invoiceValueChanges(): void {
        if (!this.lineItemsField.control || !this.paymentGrid.control) {
            return null;
        }
        const txns = (this.lineItemsField.control as GridControl).gridRows();
        const txnDate = this.txnDateField.getFormValue();

        let total = 0;
        let prepays = 0;
        let vatTotal = 0;


        if (txns) {
            for (const tRow of txns) {
                const acctId = ((tRow.get('bCodeId') as AppFormControl).value);
                const schedId = ((tRow.get('scheduleId') as AppFormControl).value);
                const sched = this.schedules.find(o => o.id === schedId);
                const acct = this.bcodes.find(o => o.id === acctId);
                const amt = tRow.get('amount').value;

                if (this.vatRegistered) {
                    const bc = this.bcodes.find( ec => ec.id === acctId);
                    if (bc) {
                        tRow.get('vatCode').setValue(bc.vatCode, {emitEvent: false});
                        if (txnDate) {
                            const rate = this.vatRateSvc.getRate(bc.vatCode, txnDate);
                            tRow.get('vatRate').setValue(rate, {emitEvent: false});

                            const vatAmount = Math.round(amt * rate) / 100
                            const typeId = this.currentTypeId();
                            if ( typeId === Txn.TYPE.PURCHASE_CN.id || typeId === Txn.TYPE.PURCHASE_CN_ACCRUAL.id ) {
                                tRow.get('vatAmount').setValue(0 - vatAmount);
                                vatTotal -= vatAmount;
                            } else {
                                tRow.get('vatAmount').setValue(vatAmount);
                                vatTotal += vatAmount;
                            }
                            total += vatAmount;
                        }
                    }
                }
                if (acct?.prepaid) {
                    prepays++;
                }
                
                if (!tRow.deleted) {
                    total += amt;
                }
                
                this.checkForSplitNeeded(tRow, acct, sched, amt);
            }
        }

        this.hasPrepays = (prepays > 0);
        this.svcFromField.control.updateValueAndValidity();
        this.svcToField.control.updateValueAndValidity();

        this.amountField.control.setValue(total);
        this.vatTotalField.control.setValue(vatTotal);
    }

    checkForSplitNeeded(tRow: GridRow, acct: BCode, sched: Schedule, amt: number) {

        if (amt > 0 && (sched === undefined) && acct.schedule.length > 1) {
            let scheds = '';
            for (const bs of acct.schedule) {
                if (scheds.length > 0) {
                    scheds += ', ';
                }
                const sched = this.schedules.find(o => o.id === bs.scheduleId);
                const pct = formatPercent(bs.percent, Field.locale);
                scheds += sched.name + ' (' + (pct) + ')';
            }
            const msg = $localize`Transactions for ${acct.name} are normally distributed as ${scheds}. Do you wish to do so`;
            this.cds.open($localize`Confirm Split`, msg, () => {
                const total = amt;
                for (const bs of acct.schedule) {
                    const txn = this.createChildItem();
                    txn.bCodeId = acct.id;
                    txn.bCode = acct;
                    txn.scheduleId = bs.scheduleId;
                    txn.schedule = this.schedules.find(o => o.id === bs.scheduleId);
                    txn.debit = total * bs.percent;
                    this.lineItemsField.control.addRow(txn, true, false);
                }
                tRow.delete();
            }, $localize`Split`);
        }
    }

    bankRequired(o: AppFormControl) {
        if (this.page.form.focusItem && this.currentTypeId() === Txn.TYPE.CASH_PURCHASE.id) {
            if ((!o.value && o.value !== 0) || (typeof o.value === 'string' && o.value.trim().length === 0)) {
                return FormError.reportError('cashPurchaseBank',
                        $localize`Required for cash purchases`);
            }
        } else {
            if (o.value) {
                return FormError.reportError('notCashPurchase',
                        $localize`Forbidden unless cash purchase`);
            }
        }
        return null;
    }

    validateServiceDates(o: AppFormControl) {
        let validationError = null;
        if (!this.svcFromField.control || !this.svcToField.control) {
            return null; // not fully initialized yet...
        }
        //console.log('Validating service dates...', {prepays: this.hasPrepays, ctl: o, value: o.value});
        if (this.hasPrepays) {
            validationError = validateDate()(o);
        } else {
            if (Field.isEmpty(o.value)) {
                validationError = null;
            } else {
                validationError = validateDate()(o);
            }
        }

        if (!Field.isEmpty(this.svcFromField.control?.value)
            && !Field.isEmpty(this.svcToField.control?.value)
            && !validationError) {
            if (this.svcToField.control.value <= this.svcFromField.control.value) {
                validationError = FormError.reportError('serviceDatesRangeWrong',
                    $localize`Service to date must be after service from date`);
            }
        }

        const fromDate = this.svcFromField.control.value;
        const toDate = this.svcToField.control.value;
        let hasStart = false;
        let hasEnd = false;

        if (!Field.isEmpty(this.svcFromField.control?.value)
            && !Field.isEmpty(this.svcToField.control?.value)
            && !validationError && this.hasPrepays) {
            for (const p of this.periods) {
                if (p.from <= toDate && p.to >= fromDate) {
                    if (!Period.open(p)) {
                        validationError = FormError.reportError('includesClosedPeriods',
                            $localize`Service dates include closed period ${p.name}`);
                        break;
                    }
                    if (p.from <= fromDate && p.to >= fromDate) {
                        hasStart = true;
                    }
                    if (p.from <= toDate && p.to >= toDate) {
                        hasEnd = true;
                    }
                }
            }
        }

        if (!hasStart && !validationError && this.hasPrepays) {
            validationError = FormError.reportError('beforeFinancialStart',
                $localize`Service starts before any financial periods are available to process it`);
        }
        if (!hasEnd && !validationError && this.hasPrepays) {
            validationError = FormError.reportError('afterFinancialEnd',
                $localize`Service ends after last financial periods available to process it`);
        }

        if (o.field.name === 'svcFrom' && !validationError && this.svcToField.control.invalid) {
            this.svcToField.control.updateValueAndValidity();
        } else if (o.field.name === 'svcTo' && !validationError && this.svcFromField.control.invalid) {
            this.svcFromField.control.updateValueAndValidity();
        }

        return validationError;
    }
}

